/** Analytics / KPI dashboards — adapts to the caller's scope:
 *  operator (self), supervisor (analytics.team.read, org-wide), exec
 *  (analytics.org.read, + business/cost). See apps/analytics. */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AppLayout } from "@/components/AppLayout";
import { PermissionGate } from "@/components/PermissionGate";
import { SessionsLineChart } from "@/components/SessionsLineChart";
import { StatCard } from "@/components/StatCard";
import { useHasPermission } from "@/lib/permissions";
import {
  FilterBar,
  MetricRows,
  ParetoBars,
  ScoreGauge,
  downloadExport,
  useAiPerformance,
  useCost,
  useInspections,
  useMe,
  useOverview,
  useRealtime,
  useScore,
  useTimeseries,
} from "@/modules/analytics";
import type { KpiFilterState } from "@/modules/analytics";

const ANY = ["analytics.read", "analytics.team.read", "analytics.org.read"] as const;

export default function AnalyticsDashboardPage() {
  const { t } = useTranslation();
  const canTeam = useHasPermission("analytics.team.read");
  const canOrg = useHasPermission("analytics.org.read");
  const canExport = useHasPermission("analytics.export");
  const [filter, setFilter] = useState<KpiFilterState>({});

  return (
    <AppLayout
      title={t("analytics.title")}
      description={t("analytics.subtitle")}
      headerAction={
        canExport ? (
          <div className="flex gap-2">
            <button type="button" className="btn btn-ghost" onClick={() => downloadExport(filter, "xlsx")}>
              {t("analytics.exportXlsx")}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => downloadExport(filter, "pdf")}>
              {t("analytics.exportPdf")}
            </button>
          </div>
        ) : undefined
      }
    >
      <PermissionGate anyOf={ANY} fallback={<p className="text-nb-text-2">{t("analytics.noAccess")}</p>}>
        <div className="space-y-6">
          <FilterBar value={filter} onChange={setFilter} />
          <RealtimeRow filter={filter} />
          {canTeam ? <TeamPanels filter={filter} canOrg={canOrg} /> : <SelfPanels filter={filter} />}
        </div>
      </PermissionGate>
    </AppLayout>
  );
}

function RealtimeRow({ filter }: { filter: KpiFilterState }) {
  const { t } = useTranslation();
  const { data } = useRealtime(filter);
  const r = data;
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
      <StatCard label={t("analytics.activeCampaigns")} value={r?.active_campaigns ?? "—"} />
      <StatCard label={t("analytics.activeAiCalls")} value={r?.active_ai_calls ?? "—"} tone="info" />
      <StatCard label={t("analytics.activeInspections")} value={r?.active_inspections ?? "—"} tone="ok" />
      <StatCard label={t("analytics.activeOperators")} value={r?.active_operators ?? "—"} />
      <StatCard label={t("analytics.completedToday")} value={r?.inspections_completed_today ?? "—"} tone="ok" />
      <StatCard label={t("analytics.appointmentsToday")} value={r?.appointments_today ?? "—"} tone="info" />
    </div>
  );
}

function TeamPanels({ filter, canOrg }: { filter: KpiFilterState; canOrg: boolean }) {
  const { t } = useTranslation();
  const inspections = useInspections(filter, true);
  const aiPerf = useAiPerformance(filter, true);
  const score = useScore(filter, true);
  const series = useTimeseries(filter, "inspections", "day", true);
  const overview = useOverview(filter, canOrg);
  const cost = useCost(filter, canOrg);

  const points = (series.data?.series ?? []).map((s) => ({ date: s.bucket, count: s.count }));

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-[1fr_1.6fr]">
        <section className="card flex flex-col items-center justify-center">
          <h2 className="mb-2 text-sm font-semibold text-nb-text">{t("analytics.score")}</h2>
          {score.data ? <ScoreGauge score={score.data} ariaLabel={t("analytics.score")} /> : <p className="text-nb-text-3">—</p>}
        </section>
        <section className="card">
          <h2 className="mb-3 text-sm font-semibold text-nb-text">{t("analytics.inspectionsOverTime")}</h2>
          <SessionsLineChart data={points} label={t("analytics.inspectionsOverTime")} />
        </section>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="card">
          <h2 className="mb-3 text-sm font-semibold text-nb-text">{t("analytics.inspections")}</h2>
          {inspections.data && <MetricRows metrics={inspections.data.kpis} />}
        </section>
        <section className="card">
          <h2 className="mb-3 text-sm font-semibold text-nb-text">{t("analytics.rejectionReasons")}</h2>
          <ParetoBars rows={inspections.data?.rejection_pareto ?? []} emptyLabel={t("analytics.noRejections")} />
        </section>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="card">
          <h2 className="mb-3 text-sm font-semibold text-nb-text">{t("analytics.aiPerformance")}</h2>
          {aiPerf.data && (
            <>
              <div className="mb-3 grid grid-cols-3 gap-2 text-center">
                <SentimentPill label={t("analytics.positive")} value={aiPerf.data.sentiment.positive} tone="ok" />
                <SentimentPill label={t("analytics.neutral")} value={aiPerf.data.sentiment.neutral} tone="info" />
                <SentimentPill label={t("analytics.negative")} value={aiPerf.data.sentiment.negative} tone="rose" />
              </div>
              <ParetoBars rows={aiPerf.data.escalation_reasons} emptyLabel={t("analytics.noEscalations")} />
            </>
          )}
        </section>
        {canOrg && (
          <section className="card">
            <h2 className="mb-3 text-sm font-semibold text-nb-text">{t("analytics.costRoi")}</h2>
            {cost.data && (
              <MetricRows
                metrics={{
                  cost_per_inspection_eur: cost.data.cost_per_inspection_eur,
                  value_per_inspection_eur: cost.data.value_per_inspection_eur,
                  estimated_roi: cost.data.estimated_roi,
                  ...(overview.data?.business ?? {}),
                }}
              />
            )}
          </section>
        )}
      </div>
    </>
  );
}

function SelfPanels({ filter }: { filter: KpiFilterState }) {
  const { t } = useTranslation();
  const { data } = useMe(filter);
  if (!data) return null;
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <section className="card">
        <h2 className="mb-3 text-sm font-semibold text-nb-text">{t("analytics.inspections")}</h2>
        <MetricRows metrics={data.inspections} />
      </section>
      <section className="card">
        <h2 className="mb-3 text-sm font-semibold text-nb-text">{t("analytics.appointments")}</h2>
        <MetricRows metrics={data.appointments} />
      </section>
      <section className="card">
        <h2 className="mb-3 text-sm font-semibold text-nb-text">{t("analytics.aiCalling")}</h2>
        <MetricRows metrics={data.ai_calling} />
      </section>
    </div>
  );
}

function SentimentPill({ label, value, tone }: { label: string; value: number; tone: "ok" | "info" | "rose" }) {
  const toneCls = {
    ok: "bg-emerald-500/15 text-emerald-300",
    info: "bg-sky-500/15 text-sky-300",
    rose: "bg-rose-500/15 text-rose-300",
  }[tone];
  return (
    <div className={`rounded-nb-lg px-2 py-3 ${toneCls}`}>
      <div className="font-mono text-lg">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}
